import { useMutation } from '@tanstack/react-query'
import { toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { formatDateTimeUTC, formatTimeToHHMMSS } from '../../../../common/helpers/DateHandlers'
import { useStateCallback } from '../../../../common/hooks/useStateCallback'
import { ICustomerDetail } from '../../../../common/interfaces'
import { ClientService } from '../../../../services/ClientService'
import { FileService } from '../../../../services/FileService'

interface IProps {
  customerDetail: ICustomerDetail
}

function getFileNameFromBucketPath(path: string) {
  return path.substring(path.lastIndexOf('/') + 1, path.length)
}

export const RiskProfileDocumentTable = ({ customerDetail }: IProps) => {
  const [fileUrl, setFileUrl] = useStateCallback('')
  const [downloadingRiskId, setDownloadingRiskId] = useStateCallback<number | null>(null)

  const downloadMutation = useMutation({
    mutationFn: (url: string) => FileService().downloadFromAzure(url),
    onSuccess: async (data) => {
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', getFileNameFromBucketPath(fileUrl))

      document.body.appendChild(link)
      link.click()

      await new Promise((resolve) => setTimeout(resolve, 1000))

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      setDownloadingRiskId(null)
    },
    onError: () => {
      setDownloadingRiskId(null)
    },
  })

  const downloadPDRMutation = useMutation({
    mutationFn: (customer_risk_id: number) =>
      ClientService().downloadCustomerPDR(customer_risk_id, customerDetail?.id),
    onSuccess: async (data) => {
      setFileUrl(data)
      downloadMutation.mutate(data)
    },
    onError: () => {
      setDownloadingRiskId(null)
    },
  })

  const handleDownloadPDR = (customer_risk_id: number) => {
    setDownloadingRiskId(customer_risk_id)
    downloadPDRMutation.mutate(customer_risk_id)
  }
  return (
    <>
      <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
        {customerDetail.customerRisks.length ? (
          <>
            <thead>
              <tr className='fw-bolder text-muted'>
                <th className='w-120px'>FECHA</th>
                <th className='w-150px'>
                  <span className='d-block'>PERFIL</span>
                  <span className='d-block'>SELECCIONADO</span>
                </th>
                <th>DOCUMENTO</th>
                <th className='text-center'>ADJUNTO</th>
                <th className='text-center'>RESPUESTAS</th>
              </tr>
            </thead>
            <tbody>
              {customerDetail.customerRisks
                .sort(
                  (a, b) =>
                    new Date(b.constancy_date).getTime() - new Date(a.constancy_date).getTime()
                )
                .map((e, i) => (
                  <tr key={i}>
                    <td className='text-muted'>
                      {e.constancy_date ? (
                        <>
                          <span className='text-muted d-block fs-5'>
                            {formatDateTimeUTC(new Date(e.constancy_date).toDateString())}
                          </span>
                          <span className='text-muted d-block fs-5'>
                            {formatTimeToHHMMSS(new Date(e.constancy_date))}
                          </span>
                        </>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td className='text-muted fs-5'>{e.customer_risk_profile}</td>
                    <td className='text-muted fs-5'>
                      {e.constancy_url ? getFileNameFromBucketPath(e.constancy_url) : '--'}
                    </td>
                    <td className='text-center'>
                      <div className='d-flex align-items-center justify-content-center'>
                        {e.constancy_url ? (
                          <div className='d-flex align-items-center  p-1 justify-content-center cursor-pointer'>
                            {downloadMutation.isLoading && e.constancy_url === fileUrl ? (
                              <div className='text-center text-muted'>
                                <div
                                  className='spinner-border'
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                  }}
                                  role='status'
                                ></div>
                              </div>
                            ) : (
                              <div
                                onClick={() => {
                                  if (downloadMutation.isLoading) {
                                    return
                                  }
                                  setFileUrl(e.constancy_url)
                                  downloadMutation.mutate(e.constancy_url)
                                }}
                                className='iconButton'
                              >
                                <i className='bi bi-download fs-3 m-2'></i>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className='text-muted'>--</span>
                        )}
                      </div>
                    </td>
                    <td className='text-center'>
                      <div className='d-flex align-items-center justify-content-center'>
                        {e.qestionnaire && e.question_answers && e.question_answers.length > 0 ? (
                          <i
                            className='bi bi-eye m-2 fs-3 cursor-pointer'
                            data-bs-toggle='modal'
                            data-bs-target={`#QuestionAnswersModal-${e.id}`}
                          />
                        ) : (
                          <span className='text-muted'>--</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </>
        ) : (
          <span className='notFound text-muted fs-5 text-center '>
            No se registró cambio de perfil
          </span>
        )}
      </table>
      {customerDetail.customerRisks.map((risk) => {
        if (!risk.qestionnaire) return null

        return (
          <div
            key={risk.id}
            className='modal fade p-5 rounded-4'
            tabIndex={-1}
            id={`QuestionAnswersModal-${risk.id}`}
            role='dialog'
          >
            <div className='modal-dialog rounded-4 modal-dialog-centered' role='document'>
              <div className='modal-content'>
                <div className='modal-header py-5 mt-2 fw-bolder'>
                  <h1 className='text-alert'>Respuestas PDR</h1>
                  <button
                    type='button'
                    className='close border-0 top-0'
                    data-bs-dismiss='modal'
                    aria-label='Close'
                  >
                    <img
                      src={`${toAbsoluteUrl('/✅ Web - Mm/Close.png')}`}
                      alt='Close icon'
                      aria-hidden='true'
                      style={{
                        width: 10,
                        height: 10,
                      }}
                    />
                  </button>
                </div>

                <div className='modal-body'>
                  <div>
                    {risk.qestionnaire.questions &&
                      risk.question_answers &&
                      risk.qestionnaire.questions.map((question, i) => {
                        const selectedOptions = question.options.filter((option) =>
                          risk.question_answers?.includes(option.id)
                        )

                        return (
                          <div key={question.id} className='pt-2'>
                            <h6 className='text-alert fs-4'>
                              {i + 1}. {question.title}
                            </h6>
                            {selectedOptions.length > 0 ? (
                              selectedOptions.map((option) => <p key={option.id}>{option.title}</p>)
                            ) : (
                              <p className='text-muted'>Sin respuesta seleccionada</p>
                            )}
                          </div>
                        )
                      })}
                  </div>
                </div>
                <div className='modal-footer'>
                  <button
                    style={{ background: 'rgb(0, 123, 195)' }}
                    className='btn text-white cursor-pointer'
                    onClick={() => handleDownloadPDR(risk.id)}
                    disabled={downloadingRiskId === risk.id || downloadPDRMutation.isLoading}
                  >
                    {downloadingRiskId === risk.id || downloadPDRMutation.isLoading
                      ? 'Descargando...'
                      : 'Descargar respuestas'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}
